"use client";

import { useState } from "react";
import { formatTimeDisplay } from "@/lib/utils/time";
import { getCurrencySymbol } from "@/lib/utils/currency";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import {
  Calendar,
  Clock,
  Building,
  MapPin,
  Pencil,
  Plus,
  Loader2,
} from "lucide-react";
import { useRouter, usePathname } from "next/navigation";
import { PricingService } from "@/lib/pricing/pricing-service-unified";
import { BOOKING_TYPE_IDS } from "@/lib/constants/booking-types";

interface ShowsCardProps {
  booking: any;
  bookingDetails: any;
  venueCurrency: string;
  venueDefaultLanguage?: string;
}

// Customer type constants (matching database)
const CUSTOMER_TYPE_IDS = {
  PRIVATE: 1,
  CORPORATE: 2,
};

export function ShowsCard({
  booking,
  bookingDetails,
  venueCurrency,
  venueDefaultLanguage = "en",
}: ShowsCardProps) {
  const router = useRouter();
  const pathname = usePathname();
  // Use venue's default language for time formatting (24h for Nordic languages)
  const locale = venueDefaultLanguage;
  const hasShow = booking.studio_show_host_bookings?.[0];
  const [isNavigating, setIsNavigating] = useState(false);

  // Extract venue ID from pathname
  const venueId = pathname?.split("/")[3];
  const bookingId = booking.id;

  // Determine the portal type
  const isBarStaff = pathname?.includes("/barstaff/");
  const isSuperAdmin = pathname?.includes("/superadmin/");

  const handleEditShow = () => {
    // Set loading state
    setIsNavigating(true);

    // Determine the correct base path (superadmin or booking staff)
    const basePath = isSuperAdmin ? "/superadmin" : "/booking";

    // Navigate to booking-new page with booking ID as query parameter
    router.push(
      `${basePath}/venue/${venueId}/booking-new?bookingId=${bookingId}`,
    );
  };

  if (!hasShow) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg font-semibold text-gray-900">
            Shows
          </CardTitle>
          {!isBarStaff && (
            <Button
              onClick={handleEditShow}
              disabled={isNavigating}
              className="bg-white border border-purple-600 text-purple-600 hover:bg-purple-50 disabled:opacity-50"
            >
              {isNavigating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Loading...
                </>
              ) : (
                <>
                  <Pencil className="h-4 w-4 mr-2" />
                  Edit booking / Add show
                </>
              )}
            </Button>
          )}
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-gray-500">
            <p>No show assigned to this booking</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-lg font-semibold text-gray-900">
          Shows
        </CardTitle>
        {!isBarStaff && (
          <Button
            onClick={handleEditShow}
            disabled={isNavigating}
            className="bg-white border border-purple-600 text-purple-600 hover:bg-purple-50 disabled:opacity-50"
          >
            {isNavigating ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Loading...
              </>
            ) : (
              <>
                <Pencil className="h-4 w-4 mr-2" />
                Edit booking / Add show
              </>
            )}
          </Button>
        )}
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Show name</TableHead>
              <TableHead>Participants</TableHead>
              <TableHead>Studio</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Start time</TableHead>
              <TableHead>End time</TableHead>
              <TableHead className="text-right">Price</TableHead>
              <TableHead className="text-right">VAT</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {booking.studio_show_host_bookings?.map(
              (showBooking: any, index: number) => {
                // Always use English for show names in admin portals
                const showInfos = showBooking.show_time_slot_pricing?.shows?.show_infos || [];
                const showInfo =
                  showInfos.find((info: any) => info.lang === 'en') ||
                  showInfos[0] || // Fallback to first if English not found
                  {};
                // Road shows have no pricing→show join, so fall back to the event
                // label captured in the booking details (the same show name).
                let roadShowEventLabel: string | null = null;
                if (
                  showBooking.booking_type_id === BOOKING_TYPE_IDS.ON_ROAD &&
                  booking.details
                ) {
                  try {
                    const parsed =
                      typeof booking.details === "string"
                        ? JSON.parse(booking.details)
                        : booking.details;
                    roadShowEventLabel = parsed?.eventTypeLabel || null;
                  } catch {
                    // details isn't JSON — ignore.
                  }
                }
                const studio = showBooking.studios || {};
                const sessionDate = new Date(showBooking.show_session_timeslot);
                const hasValidSessionDate = !Number.isNaN(sessionDate.getTime());
                const sessionDateLabel = hasValidSessionDate
                  ? sessionDate.toLocaleDateString("en-GB", {
                      weekday: "short",
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    })
                  : "Unknown date";
                const startTime = formatTimeDisplay(
                  sessionDate.toTimeString().slice(0, 5),
                  locale
                );
                // For road show bookings, parse the end time from special_request
                let endDate;
                let endTime;

                if (showBooking.booking_type_id === BOOKING_TYPE_IDS.ON_ROAD && booking.special_request) {
                  // Try to parse the time range from special_request
                  const timeMatch = booking.special_request.match(/Time:\s*(\d{1,2}:\d{2})\s*-\s*(\d{1,2}:\d{2})/);
                  if (timeMatch && timeMatch[2]) {
                    // Parse the end time from the special request
                    const [endHours, endMinutes] = timeMatch[2].split(':').map(Number);
                    endDate = new Date(sessionDate);
                    endDate.setHours(endHours, endMinutes, 0, 0);
                    endTime = formatTimeDisplay(
                      endDate.toTimeString().slice(0, 5),
                      locale
                    );
                  } else {
                    // Fallback to default duration
                    const showDuration =
                      showBooking.show_time_slot_pricing?.shows?.duration_minutes ||
                      60;
                    endDate = new Date(
                      sessionDate.getTime() + showDuration * 60000,
                    );
                    endTime = formatTimeDisplay(
                      endDate.toTimeString().slice(0, 5),
                      locale
                    );
                  }
                } else {
                  // Regular booking - use custom duration if available, otherwise show duration
                  const showDuration =
                    showBooking.custom_duration_minutes ||
                    showBooking.show_time_slot_pricing?.shows?.duration_minutes ||
                    60;
                  endDate = new Date(
                    sessionDate.getTime() + showDuration * 60000,
                  );
                  endTime = formatTimeDisplay(
                    endDate.toTimeString().slice(0, 5),
                    locale
                  );
                }
                const pricing =
                  showBooking.show_time_slot_pricing?.show_price_points
                    ?.pricings || {};

                // Use new DB fields if available, otherwise fallback to pricing
                // Parse the base_amount_excl_vat as a number (it's stored as string in DB)
                const baseAmountExclVat = parseFloat(booking.base_amount_excl_vat) || 0;
                const unitPrice = booking.base_amount_excl_vat !== undefined && booking.base_amount_excl_vat !== null
                  ? booking.price_type_id === 2
                    ? baseAmountExclVat / booking.participant_count
                    : baseAmountExclVat
                  : pricing.price || 0;

                const vatPercentage =
                  booking.vat_rate || pricing.vat_percentage || 0;

                // Note: Price modifier is already applied to booking prices in the database
                // No need to apply it again here

                // Use PricingService for consistent calculations
                const priceCalculation = PricingService.calculate({
                  unitPrice: unitPrice,
                  vatPercentage: vatPercentage,
                  participants:
                    booking.price_type_id === 2 ? booking.participant_count : 1,
                  customerType:
                    booking.customer_type_id === CUSTOMER_TYPE_IDS.PRIVATE
                      ? "private"
                      : "corporate",
                  priceType:
                    booking.price_type_id === 2 ? "per_person" : "fixed",
                });

                // For corporate, calculate what VAT would be (for display purposes)
                const invoiceVatAmount =
                  booking.customer_type_id === CUSTOMER_TYPE_IDS.CORPORATE
                    ? priceCalculation.baseAmount * (vatPercentage / 100)
                    : priceCalculation.vatAmount;

                const displayVatNote =
                  booking.customer_type_id === CUSTOMER_TYPE_IDS.CORPORATE &&
                  vatPercentage > 0;

                // Check if this is not the first show to determine if we should show dashes
                const isFirstShow = index === 0;

                return (
                  <TableRow key={showBooking.id || index}>
                    <TableCell className="font-medium">
                      {showInfo.name || roadShowEventLabel || "Unknown Show"}
                    </TableCell>
                    <TableCell>
                      {isFirstShow ? (booking.participant_count || "-") : "-"}
                    </TableCell>
                    <TableCell>{studio.name || "Unknown Studio"}</TableCell>
                    <TableCell>{sessionDateLabel}</TableCell>
                    <TableCell>{startTime}</TableCell>
                    <TableCell>{endTime}</TableCell>
                    <TableCell className="text-right">
                      {isFirstShow ? (
                        <>{priceCalculation.baseAmount.toFixed(2)} {getCurrencySymbol(venueCurrency)}</>
                      ) : (
                        "-"
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {isFirstShow ? (
                        displayVatNote ? (
                          <span>
                            <span>
                              {invoiceVatAmount.toFixed(2)} {getCurrencySymbol(venueCurrency)}
                            </span>
                            <span className="text-muted-foreground italic text-xs ml-1">
                              (Invoice)
                            </span>
                          </span>
                        ) : (
                          <span>
                            {invoiceVatAmount.toFixed(2)} {getCurrencySymbol(venueCurrency)}
                          </span>
                        )
                      ) : (
                        "-"
                      )}
                    </TableCell>
                  </TableRow>
                );
              },
            )}
          </TableBody>
        </Table>
        {booking.studio_show_host_bookings && booking.studio_show_host_bookings.length > 1 && (
          <p className="text-sm text-muted-foreground mt-4">
            Note: Participants, price, and VAT are shown only for the first show when multiple shows share the same values.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
